import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import QRCode from 'react-qr-code';
import { Card, CardContent } from '../components/ui/card';
import { 
  ShieldCheck, CheckCircle2, Ship, Anchor, 
  Calendar, MapPin, AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicCertificatePage() {
  const { shareToken } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCertificate();
  }, [shareToken]);

  const fetchCertificate = async () => {
    try {
      const response = await axios.get(`${API}/public/certificate/${shareToken}`);
      setCertificate(response.data);
    } catch (err) {
      if (err.response?.status === 410) {
        setError('This certificate link has expired.');
      } else if (err.response?.status === 404) {
        setError('Certificate not found.');
      } else {
        setError('Failed to load certificate.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#2B3A67] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center p-6">
        <Card className="max-w-md w-full border-2 border-[#E2E8F0]">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 text-[#E5A000] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#2B3A67] mb-2">Certificate Unavailable</h2>
            <p className="text-[#4A5568]">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F4F8] py-8 px-6">
      <div className="max-w-md mx-auto">
        {/* Logo Header */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#2B3A67] rounded-xl flex items-center justify-center">
            <Anchor className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-black text-[#2B3A67] tracking-tight font-['Outfit']">TRUSTED</h1>
            <h1 className="text-xl font-black text-[#3CB371] tracking-tight font-['Outfit'] -mt-1">BOATER</h1>
          </div>
        </div>

        {/* Certificate Card */}
        <Card className="border-2 border-[#3CB371] bg-white shadow-xl overflow-hidden">
          {/* Header Banner */}
          <div className="bg-[#2B3A67] px-6 py-4 text-white text-center">
            <h2 className="font-black text-lg font-['Outfit']">SAFETY COMPLIANCE CERTIFICATE</h2>
            {certificate.is_verified && (
              <div className="flex items-center justify-center gap-2 mt-2 text-[#3CB371]">
                <ShieldCheck className="w-5 h-5" />
                <span className="font-bold text-sm">VERIFIED</span>
              </div>
            )}
          </div>

          <CardContent className="p-6 space-y-6">
            {/* Certificate ID */}
            <div className="text-center">
              <p className="text-xs font-bold text-[#4A5568] uppercase tracking-wide">Certificate ID</p>
              <p className="text-lg font-black text-[#2B3A67] font-mono">{certificate.certificate_id}</p>
            </div>

            {/* Compliance Score */}
            <div className="text-center p-6 bg-[#E8F6ED] rounded-2xl">
              <p className={`text-5xl font-black font-['Outfit'] ${
                certificate.compliance_score === 100 ? 'text-[#3CB371]' : 'text-[#E5A000]'
              }`}>
                {certificate.compliance_score}%
              </p>
              <p className="text-[#2A8A53] font-bold mt-2">
                {certificate.is_compliant ? 'FWC COMPLIANT' : 'NOT COMPLIANT'}
              </p>
            </div>

            {/* Boater & Vessel Info */}
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-[#E2E8F0]">
                <span className="text-[#4A5568]">Boater</span>
                <span className="font-bold text-[#2B3A67]">{certificate.boater_name}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-[#E2E8F0]">
                <span className="text-[#4A5568]">Vessel</span>
                <span className="font-bold text-[#2B3A67]">{certificate.vessel_name}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-[#E2E8F0]">
                <span className="text-[#4A5568]">Vessel Class</span>
                <span className="font-bold text-[#2B3A67]">Class {certificate.vessel_class}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-[#E2E8F0]">
                <span className="text-[#4A5568]">Location</span>
                <span className="font-bold text-[#2B3A67] capitalize">{certificate.location}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-[#E2E8F0]">
                <span className="text-[#4A5568]">Time</span>
                <span className="font-bold text-[#2B3A67] capitalize">{certificate.time_of_day}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-[#E2E8F0]">
                <span className="text-[#4A5568]">Items Verified</span>
                <span className="font-bold text-[#3CB371]">
                  {certificate.verified_items_count}/{certificate.total_items_count}
                </span>
              </div>
            </div>

            {/* Date */}
            <div className="flex items-center justify-center gap-2 text-sm text-[#4A5568]">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(certificate.checklist_date)}</span>
            </div>

            {/* Verification Code & QR */}
            <div className="text-center pt-4 border-t border-[#E2E8F0]">
              <p className="text-xs text-[#4A5568]">Verification Code</p>
              <p className="font-mono font-bold text-[#2B3A67] text-lg">{certificate.verification_code}</p>
            </div>

            {/* QR Code */}
            <div className="flex flex-col items-center pt-2" data-testid="public-certificate-qr-code">
              <div className="bg-white p-3 rounded-xl border border-[#E2E8F0]">
                <QRCode
                  value={window.location.href}
                  size={120}
                  level="H"
                  fgColor="#2B3A67"
                />
              </div>
              <p className="text-[10px] text-[#4A5568] mt-2">Scan to verify</p>
            </div>

            {/* Footer */}
            <div className="text-center text-xs text-[#4A5568] pt-4">
              <p>This certificate verifies compliance with</p>
              <p className="font-bold">Florida FWC Boating Safety Requirements</p>
            </div>
          </CardContent>
        </Card>

        {/* Powered by */}
        <div className="text-center mt-6 text-sm text-[#4A5568]">
          <p>Powered by Trusted Boater</p>
          <p className="text-xs">trustedboater.com</p>
        </div>
      </div>
    </div>
  );
}
